/**
 * Appointments Service
 * API calls for appointment operations
 */

import { API_ENDPOINTS } from '@/constants/api';
import { apiClient, ApiResponse } from './apiClient';

export type AvailabilityType = 'TELEMEDICINE' | 'HOME' | 'CLINIC';

export interface AppointmentDTO {
  id: string;
  doctorId: string;
  patientId: string;
  date: string;
  time: string;
  appointmentDate: string; // ISO string from backend
  rescheduleRequestedDate?: string;
  originalAppointmentId?: string | null;
  followUpAppointmentId?: string | null;
  duration?: number; // em minutos
  reason?: string;
  status: 'requested' | 'scheduled' | 'completed' | 'cancelled' | 'no-show' | 'in_transit' | 'reschedule_requested' | 'CONFIRMADO' | 'RESERVADO_PENDENTE';
  notes?: string;
  videoCallUrl?: string;
  type?: 'home' | 'video' | 'clinic';
  appointmentType?: 'TELEMEDICINA' | 'DOMICILIAR' | 'CONSULTORIO';
  address?: {
    street: string;
    number: string;
    complement?: string;
    neighborhood: string;
    city: string;
    state: string;
    zipCode: string;
  };
  createdAt: string;
  updatedAt: string;
  doctor?: {
    id: string;
    crm: string;
    specialty: string;
    user?: {
      name: string;
    };
  };
  patient?: {
    id: string;
    name: string;
    patientProfile?: {
      dateOfBirth?: string;
      street?: string;
      number?: string;
      city?: string;
      state?: string;
    };
    user?: {
      name: string;
    };
  };
}

export interface CreateAppointmentDTO {
  doctorId: string;
  patientId: string;
  date: string;
  time: string;
  duration?: number;
  reason?: string;
  notes?: string;
  type?: 'home' | 'video' | 'clinic';
  appointmentType?: 'TELEMEDICINA' | 'DOMICILIAR' | 'CONSULTORIO';
  paymentId?: string;
  address?: {
    street: string;
    number: string;
    complement?: string;
    neighborhood: string;
    city: string;
    state: string;
    zipCode: string;
  };
}

export interface UpdateAppointmentDTO {
  date?: string;
  time?: string;
  duration?: number;
  status?: 'requested' | 'scheduled' | 'completed' | 'cancelled' | 'no-show' | 'in_transit' | 'reschedule_requested' | 'CONFIRMADO' | 'RESERVADO_PENDENTE';
  notes?: string;
  videoCallUrl?: string;
}

/**
 * List all appointments
 */
export async function listAppointments(): Promise<ApiResponse<AppointmentDTO[]>> {
  return apiClient.get(API_ENDPOINTS.APPOINTMENTS.LIST);
}

/**
 * Get appointment by ID
 */
export async function getAppointmentById(
  id: string,
): Promise<ApiResponse<AppointmentDTO>> {
  return apiClient.get(API_ENDPOINTS.APPOINTMENTS.GET(id));
}

/**
 * Get appointments by doctor (aceita userId do médico ou id do perfil — backend resolve).
 */
export async function getAppointmentsByDoctor(
  doctorId: string,
  startDate?: string,
  endDate?: string,
): Promise<ApiResponse<AppointmentDTO[]>> {
  let url = API_ENDPOINTS.APPOINTMENTS.GET_BY_DOCTOR(doctorId);
  const params = new URLSearchParams();
  
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  
  const queryString = params.toString();
  if (queryString) {
    url += `?${queryString}`;
  }
  
  return apiClient.get(url);
}

/**
 * Get appointments of the authenticated doctor (preferível na Agenda).
 */
export async function getMyDoctorAppointments(
  startDate?: string,
  endDate?: string,
): Promise<ApiResponse<AppointmentDTO[]>> {
  let url = API_ENDPOINTS.APPOINTMENTS.GET_MY_DOCTOR;
  const params = new URLSearchParams();

  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);

  const queryString = params.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  return apiClient.get(url);
}

/**
 * Get appointments by patient
 */
export async function getAppointmentsByPatient(
  patientId: string,
): Promise<ApiResponse<AppointmentDTO[]>> {
  console.log('[AppointmentsService] getAppointmentsByPatient called with patientId:', patientId);
  console.log('[AppointmentsService] Type of patientId:', typeof patientId);
  console.log('[AppointmentsService] Length:', patientId.length);
  const endpoint = API_ENDPOINTS.APPOINTMENTS.GET_BY_PATIENT(patientId);
  console.log('[AppointmentsService] Generated endpoint:', endpoint);
  return apiClient.get(endpoint);
}

/**
 * Create appointment
 */
export async function createAppointment(
  data: CreateAppointmentDTO,
): Promise<ApiResponse<AppointmentDTO>> {
  console.log('[AppointmentService] createAppointment called with data:', data);
  console.log('[AppointmentService] doctorId value:', data.doctorId, '(type:', typeof data.doctorId, ')');
  console.log('[AppointmentService] patientId value:', data.patientId, '(type:', typeof data.patientId, ')');
  const response = apiClient.post(API_ENDPOINTS.APPOINTMENTS.CREATE, data);
  console.log('[AppointmentService] Response promise created');
  return response;
}

/**
 * Update appointment
 */
export async function updateAppointment(
  id: string,
  data: UpdateAppointmentDTO,
): Promise<ApiResponse<AppointmentDTO>> {
  return apiClient.put(API_ENDPOINTS.APPOINTMENTS.UPDATE(id), data);
}

/**
 * Delete appointment
 */
export async function deleteAppointment(id: string): Promise<ApiResponse<void>> {
  return apiClient.delete(API_ENDPOINTS.APPOINTMENTS.DELETE(id));
}

/**
 * Cancel appointment
 */
export async function cancelAppointment(
  id: string,
  reason?: string,
): Promise<ApiResponse<AppointmentDTO>> {
  return updateAppointment(id, {
    status: 'cancelled',
  });
}

/**
 * Accept appointment solicitation
 */
export async function acceptAppointment(
  id: string,
): Promise<ApiResponse<AppointmentDTO>> {
  return apiClient.patch(API_ENDPOINTS.APPOINTMENTS.ACCEPT(id));
}

/**
 * Refuse appointment solicitation
 */
export async function refuseAppointment(
  id: string,
): Promise<ApiResponse<AppointmentDTO>> {
  return apiClient.patch(API_ENDPOINTS.APPOINTMENTS.REFUSE(id));
}

/**
 * Get available slots for a doctor
 */
export async function getAvailableSlots(
  doctorId: string,
  date: string,
  type?: AvailabilityType,
): Promise<ApiResponse<{ time: string; type: AvailabilityType }[]>> {
  const params = new URLSearchParams({ date });
  if (type) params.set('type', type);
  return apiClient.get(`${API_ENDPOINTS.APPOINTMENTS.BASE}/available-slots/${doctorId}?${params.toString()}`);
}

/**
 * Request appointment reschedule
 */
export async function rescheduleAppointment(
  id: string,
  data: { date: string; time?: string },
): Promise<ApiResponse<AppointmentDTO>> {
  return apiClient.patch(API_ENDPOINTS.APPOINTMENTS.RESCHEDULE(id), data);
}

/**
 * Approve reschedule request
 */
export async function approveReschedule(
  id: string,
): Promise<ApiResponse<AppointmentDTO>> {
  return apiClient.patch(API_ENDPOINTS.APPOINTMENTS.APPROVE_RESCHEDULE(id));
}

/**
 * Reject reschedule request
 */
export async function rejectReschedule(
  id: string,
): Promise<ApiResponse<AppointmentDTO>> {
  return apiClient.patch(API_ENDPOINTS.APPOINTMENTS.REJECT_RESCHEDULE(id));
}
